import * as React from 'react';

type VisualViewportMetrics = {
  height?: number;
  keyboardInset: number;
};

function getVisualViewportMetrics(): VisualViewportMetrics {
  if (typeof window === 'undefined') {
    return {keyboardInset: 0};
  }

  const viewport = window.visualViewport;
  const height = viewport?.height;

  return {
    height,
    keyboardInset: Math.max(
      0,
      window.innerHeight - (height ?? window.innerHeight) - (viewport?.offsetTop ?? 0),
    ),
  };
}

function useVisualViewportMetrics(enabled: boolean): VisualViewportMetrics {
  const [metrics, setMetrics] = React.useState<VisualViewportMetrics>(() =>
    getVisualViewportMetrics(),
  );

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let animationFrameId: number | undefined;
    const viewport = window.visualViewport;
    const updateMetrics = () => setMetrics(getVisualViewportMetrics());
    const handleViewportChange = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(updateMetrics);
    };

    updateMetrics();
    window.addEventListener('resize', handleViewportChange);
    viewport?.addEventListener('resize', handleViewportChange);
    viewport?.addEventListener('scroll', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      viewport?.removeEventListener('resize', handleViewportChange);
      viewport?.removeEventListener('scroll', handleViewportChange);
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [enabled]);

  return metrics;
}

export {useVisualViewportMetrics};
